import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FiMenu, FiX, FiPhone, FiMail } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { useAnalytics } from '../hooks/useAnalytics';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { trackNavigation } = useAnalytics();

  const handleNavClick = (destination) => {
    const currentPage = window.location.pathname === '/' ? 'home' : window.location.pathname.slice(1);
    trackNavigation(currentPage, destination === '/' ? 'home' : destination.slice(1));
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { name: 'Início', path: '/' },
    { name: 'Serviços', path: '/services' },
  ];

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed w-full z-50 transition-all duration-300 ${
        isOpen
          ? 'bg-white shadow-sm border-b border-gray-100'
          : scrolled
            ? 'bg-black/40 backdrop-blur-xl backdrop-saturate-150 border-b border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.18)]'
            : 'bg-transparent'
        }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 lg:h-20">
          {/* Logo */}
          <Link to="/">
            <motion.div
              whileHover={{ scale: 1.03 }}
              className="flex items-center space-x-2 cursor-pointer"
            >
              <div>
                <h1 className={`text-2xl font-display font-bold tracking-tight ${
                  isOpen ? 'text-gray-900' : 'text-white'
                }`}>
                  AyTech<span className="font-light italic">.</span>
                </h1>
                <p className={`text-xs -mt-1 tracking-widest uppercase ${
                  isOpen ? 'text-gray-500' : 'text-white/70'
                }`}>Soluções Inovadoras</p>
              </div>
            </motion.div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                onClick={() => handleNavClick(item.path, item.name)}
                className={`relative px-3 py-2 text-sm font-medium tracking-wide transition-colors duration-300 ${
                  location.pathname === item.path
                    ? 'text-white font-semibold'
                    : 'text-white/80 hover:text-white'
                  }`}
              >
                {item.name}
                {location.pathname === item.path && (
                  <motion.div
                    layoutId="activeIndicator"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-white"
                  />
                )}
              </Link>
            ))}
          </div>

          {/* Contact Info & CTA */}
          <div className="hidden lg:flex items-center space-x-4">
            <div className="flex items-center space-x-4 text-sm">
              <div className="flex items-center space-x-1 text-white/80">
                <FiPhone className="w-4 h-4" />
                <span>(41) 9 8771-8296</span>
              </div>
              <div className="flex items-center space-x-1 text-white/80">
                <FiMail className="w-4 h-4" />
                <span>contato@aytech.online</span>
              </div>
            </div>
            <Link to="/portal">
              <motion.button
                id="btn-central-assinantes"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                className="px-5 py-2 rounded-full font-medium transition-all duration-300 btn-hover border text-sm border-white/70 text-white hover:bg-white hover:text-gray-900"
              >
                Central de Assinantes
              </motion.button>
            </Link>
            <Link to="/contact">
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                className="px-6 py-2 rounded-full font-medium transition-all duration-300 btn-hover text-sm bg-white text-gray-900 hover:bg-gray-100"
              >
                Fale Conosco
              </motion.button>
            </Link>
          </div>

          {/* Mobile menu button */}
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsOpen(!isOpen)}
            aria-label={isOpen ? 'Fechar menu' : 'Abrir menu'}
            aria-expanded={isOpen}
            className={`lg:hidden p-3 min-w-12 min-h-12 rounded-lg transition-all duration-300 border touch-target ${
              isOpen
                ? 'text-gray-900 bg-white hover:bg-gray-50 border-gray-200 shadow-sm'
                : 'text-white bg-white/10 hover:bg-white/20 border-white/30 backdrop-blur-sm'
            }`}
          >
            {isOpen ? <FiX className="w-6 h-6" /> : <FiMenu className="w-6 h-6" />}
          </motion.button>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden overflow-hidden"
            >
              <div className="px-2 pt-2 pb-6 space-y-1 bg-white rounded-b-lg border-t border-gray-100">
                {navItems.map((item) => (
                  <Link
                    key={item.name}
                    to={item.path}
                    onClick={() => setIsOpen(false)}
                    className={`block px-3 py-2 text-base font-medium rounded-lg transition-colors duration-300 ${
                      location.pathname === item.path
                        ? 'text-gray-900 bg-gray-100 font-semibold'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                  >
                    {item.name}
                  </Link>
                ))}
                <div className="pt-4 mt-4 border-t border-gray-100">
                  <div className="flex flex-col space-y-2 text-sm text-gray-600">
                    <div className="flex items-center space-x-2">
                      <FiPhone className="w-4 h-4" />
                      <span>(41) 9 8771-8296</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <FiMail className="w-4 h-4" />
                      <span>aytech.online@gmail.com</span>
                    </div>
                  </div>
                  <Link to="/portal" onClick={() => setIsOpen(false)}>
                    <motion.button
                      whileTap={{ scale: 0.95 }}
                      id="btn-central-assinantes-mobile"
                      className="w-full mt-4 border border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white px-4 py-2 rounded-full font-medium transition-all duration-300"
                    >
                      Central de Assinantes
                    </motion.button>
                  </Link>
                  <Link to="/contact">
                    <motion.button
                      whileTap={{ scale: 0.95 }}
                      className="w-full mt-2 bg-gray-900 hover:bg-gray-700 text-white px-4 py-2 rounded-full font-medium transition-colors duration-300"
                    >
                      Fale Conosco
                    </motion.button>
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
};

export default Navbar;
